import { useEffect, useRef } from 'react';
import { useTelegram } from '@/hooks/useTelegram';

// ════════════════════════════════════════════════════════════════
// CameraCapture v2 — native input[type=file] вместо getUserMedia
//
// ПОЧЕМУ:
// 1. Telegram WebView Android повторно показывает prompt разрешения
//    камеры при КАЖДОМ вызове getUserMedia() даже в одной сессии.
//    Это известный баг Telegram, фикс ещё не в production.
// 2. На некоторых Samsung устройствах getUserMedia вообще нестабилен.
// 3. Native input file capture использует системную камеру ОС
//    напрямую (как WhatsApp/Instagram) — НЕТ webview prompt.
//
// ТЕРЯЕМ:
// - Свой UI overlay с превью и большой белой кнопкой затвора
// ПРИОБРЕТАЕМ:
// - Стабильную работу на 100% устройств включая Samsung
// - Знакомый UX (системная камера телефона как в любом приложении)
// - НЕТ prompt разрешения камеры — нативная камера ОС не требует
// - Лучшее качество фото (HDR, стабилизация и т.д. из коробки)
//
// Сохранили существующую логику сжатия — фото уменьшается до 1600px
// по длинной стороне с JPEG quality 0.82 для оптимального размера/качества.
// ════════════════════════════════════════════════════════════════

interface Props {
  /**
   * СЧЁТЧИК нажатий, а не флаг «открыто».
   *
   * Раньше здесь был boolean open. Если пользователь уходил из системной камеры,
   * не сделав снимок, событие focus в Telegram WebView могло не прийти — и флаг
   * оставался true. Следующее нажатие ставило true повторно, React не видел
   * изменения, эффект не перезапускался, камера не открывалась. Кнопка выглядела
   * мёртвой до размонтирования экрана. Счётчик даёт новое значение на каждое
   * нажатие, поэтому залипнуть не может в принципе.
   */
  trigger: number;
  /** Камеру закрыли БЕЗ снимка (отмена). После удачного снимка не вызывается. */
  onClose: () => void;
  onCapture: (file: File) => void;
}

// Параметры сжатия (как в прошлой версии — баланс качества и веса)
const MAX_SIZE = 1600;
const JPEG_QUALITY = 0.82;

export function CameraCapture({ trigger, onClose, onCapture }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelTimerRef = useRef<number | null>(null);
  const interactedRef = useRef(false);
  const { haptic } = useTelegram();

  // Каждое новое значение trigger = новое нажатие на кнопку съёмки
  useEffect(() => {
    if (!trigger) return;
    interactedRef.current = false;
    let onFocus: (() => void) | null = null;

    // Небольшая задержка чтобы input точно был в DOM
    const t = setTimeout(() => {
      if (inputRef.current) {
        // Сбрасываем ДО клика: иначе повторный выбор того же файла
        // не вызовет onChange, и кнопка снова будет выглядеть мёртвой
        inputRef.current.value = '';
        inputRef.current.click();
      }
      haptic('light');

      // Защита от «юзер закрыл системную камеру не сделав фото»:
      // input file НЕ срабатывает onChange при отмене.
      // Слушаем focus — он возвращается когда камера закрыта.
      onFocus = () => {
        if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = window.setTimeout(() => {
          if (!interactedRef.current) {
            console.log('[CameraCapture] cancelled by user (no file picked)');
            onClose();
          }
        }, 500);
      };
      window.addEventListener('focus', onFocus, { once: true });
    }, 50);

    return () => {
      clearTimeout(t);
      if (cancelTimerRef.current) {
        clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
      // Слушатель мог не сработать ни разу — снимаем сами,
      // иначе они копятся с каждым нажатием
      if (onFocus) window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    interactedRef.current = true;
    const file = e.target.files?.[0];

    // Сбрасываем value чтобы можно было выбрать тот же файл ещё раз
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (!file) {
      console.log('[CameraCapture] no file selected');
      onClose();
      return;
    }

    haptic('medium');

    // onClose здесь НЕ зовём: он значит «закрыли без снимка» (11.09.2026).
    // Раньше он стоял в finally и срабатывал сразу после onCapture. Разбор
    // фото это не ломало — режим передаётся в него аргументом, — но AddMeal
    // по onClose сбрасывает photoMode, и всё, что проверяет photoMode ===
    // 'dish', пропадало только у съёмки с камеры: кнопка «Поделиться» под
    // фото и карточка в тосте после сохранения. Из галереи onClose не
    // вызывается вовсе — отсюда «из галереи работает, с камеры нет».
    try {
      const compressed = await compressImage(file);
      onCapture(compressed);
    } catch (err) {
      console.error('[CameraCapture] compression failed:', err);
      onCapture(file);
    }
  }

  // Компонент НЕ рендерит UI overlay.
  // Только скрытый <input> который программно кликается.
  // Системная камера ОС открывается поверх приложения.
  return (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      capture="environment"
      onChange={handleChange}
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
}

// ════════════════════════════════════════════════════════════════
// СЖАТИЕ ФОТО
// Уменьшаем до MAX_SIZE по длинной стороне + JPEG quality 0.82
// ════════════════════════════════════════════════════════════════
async function compressImage(file: File): Promise<File> {
  const startedAt = performance.now();
  const originalSize = file.size;

  const img = await loadImage(file);
  const sourceWidth = img.naturalWidth;
  const sourceHeight = img.naturalHeight;

  if (!sourceWidth || !sourceHeight) {
    console.warn('[CAMERA_COMPRESS] empty image size, returning original');
    return file;
  }

  // Если фото уже маленькое — не сжимаем
  if (sourceWidth <= MAX_SIZE && sourceHeight <= MAX_SIZE && originalSize < 500_000) {
    console.log('[CAMERA_COMPRESS] image already small, skipping', {
      size: sourceWidth + 'x' + sourceHeight,
      kb: Math.round(originalSize / 1024),
    });
    return file;
  }

  const scale = Math.min(1, MAX_SIZE / Math.max(sourceWidth, sourceHeight));
  const targetWidth = Math.round(sourceWidth * scale);
  const targetHeight = Math.round(sourceHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    console.warn('[CAMERA_COMPRESS] failed to get canvas context');
    return file;
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  if (img.src.startsWith('blob:')) {
    URL.revokeObjectURL(img.src);
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
  });

  if (!blob) {
    console.warn('[CAMERA_COMPRESS] toBlob returned null, returning original');
    return file;
  }

  const compressionMs = Math.round(performance.now() - startedAt);
  console.log('[CAMERA_COMPRESS]', {
    originalSize: sourceWidth + 'x' + sourceHeight,
    originalKb: Math.round(originalSize / 1024),
    compressedSize: targetWidth + 'x' + targetHeight,
    compressedKb: Math.round(blob.size / 1024),
    savedKb: Math.round((originalSize - blob.size) / 1024),
    ratio: Math.round((1 - blob.size / originalSize) * 100) + '%',
    quality: JPEG_QUALITY,
    compressionMs,
  });

  return new File([blob], 'food_' + Date.now() + '.jpg', { type: 'image/jpeg' });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}
